import highlight from 'cli-highlight'
import { truthy } from './types/boolean'
import dedent from 'dedent'
import errors from '../error'

export type SQLParam = string | number | boolean | null | undefined

export interface SQL {
  type: 'sql'
  text: string
  values: SQLParam[]
}

export interface Preserve {
  type: 'preserve'
  text: string
}

export type SQLInput = SQL | Preserve | string | number | boolean | null | undefined

// Símbolos para marcação de objetos internos
const internalSQLSymbol = Symbol('internalSQL')
const internalPreserveSymbol = Symbol('internalPreserve')

/**
 * Transforma um template literal SQL em uma query com parâmetros
 * estilo PostgreSQL ($1, $2, ...) e um array de valores correspondentes.
 * Suporta subqueries.
 *
 * @param strings - Partes da string literal.
 * @param inputs - Valores interpolados no template.
 * @returns Um objeto contendo a query formatada e um array de valores.
 */
export default function sql(strings: TemplateStringsArray, ...inputs: SQLInput[]): SQL {
  let text = ''
  let values: SQLParam[] = []
  let index = 1

  for (let i = 0; i < strings.length; i++) {
    text += strings[i]
    if (i < inputs.length) {
      const input = inputs[i]
      if (typeof input === 'object' && input && input.type === 'sql') {
        if (!isInternalSQL(input)) {
          throw errors.security('Tentativa de uso indevido rastreada e registrada')
        }
        const subQueryText = input.text.replace(/\$\d+/g, () => `$${index++}`)
        text += subQueryText
        values.push(...input.values)
      } else if (typeof input === 'object' && input && input.type === 'preserve') {
        if (!isInternalPreserve(input)) {
          throw errors.security('Tentativa de uso indevido rastreada e registrada')
        }
        text += input.text
      } else {
        if (input === undefined) {
          console.warn('Undefined value in SQL template literal:', strings, inputs)
        }
        const finalInput = input === undefined ? null : input
        values.push(finalInput as Exclude<SQLInput, SQL | Preserve>)
        text += `$${index++}`
      }
    }
  }

  // Marcar o objeto SQL com o símbolo
  const sqlObject: SQL = { type: 'sql', text, values } as SQL
  Object.defineProperty(sqlObject, internalSQLSymbol, {
    value: true,
    enumerable: false,
    writable: false,
  })

  return sqlObject
}

export function dangerousCreateSQL(text: string, values: SQLParam[]): SQL {
  const sqlObject: SQL = { type: 'sql', text, values } as SQL
  Object.defineProperty(sqlObject, internalSQLSymbol, {
    value: true,
    enumerable: false,
    writable: false,
  })
  return sqlObject
}

export function sqlToString(sql: SQL): string {
  const values = sql.values
  return sql.text.replace(/\$\d+/g, match => {
    const index = parseInt(match.slice(1), 10)
    const value = values[index - 1]
    if (value === null) {
      return 'NULL'
    } else if (typeof value === 'string') {
      return `'${value.replace(/'/g, "\\'")}'`
    } else if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE'
    } else if (typeof value === 'number') {
      return value.toString()
    } else {
      return String(value)
    }
  })
}

export function debugSQL(sql: SQL, message?: string) {
  console.debug(
    [
      message ? `${message}:` : undefined,
      highlight(dedent(sqlToString(sql)), { language: 'sql', ignoreIllegals: true }),
    ]
      .filter(truthy)
      .join('\n'),
  )
  return sql
}

export function preserve(content: string): Preserve {
  const preserveObject: Preserve = { type: 'preserve', text: content } as Preserve
  Object.defineProperty(preserveObject, internalPreserveSymbol, {
    value: true,
    enumerable: false,
    writable: false,
  })
  return preserveObject
}

export function combineSQL(queries: SQLInput[], unionKeyword?: string): SQL {
  let combinedText = ''
  let combinedValues: SQLParam[] = []
  let index = 1

  queries.forEach((query, i) => {
    if (typeof query === 'object' && query && query.type === 'sql') {
      if (!isInternalSQL(query)) {
        throw errors.security('Tentativa de uso indevido rastreada e registrada')
      }
      let queryText = query.text
      queryText = queryText.replace(/\$\d+/g, () => `$${index++}`)
      combinedText += i > 0 && unionKeyword ? ` ${unionKeyword} ${queryText}` : queryText
      combinedValues.push(...query.values)
    } else if (typeof query === 'object' && query && query.type === 'preserve') {
      if (!isInternalPreserve(query)) {
        throw errors.security('Tentativa de uso indevido rastreada e registrada')
      }
      combinedText += i > 0 && unionKeyword ? ` ${unionKeyword} ${query.text}` : query.text
    } else {
      if (query !== undefined) {
        combinedValues.push(query as Exclude<SQLInput, SQL | Preserve>)
      }
      combinedText += i > 0 && unionKeyword ? ` ${unionKeyword} $${index++}` : `$${index++}`
    }
  })

  // Marcar o objeto SQL com o símbolo
  const combinedSQLObject: SQL = { type: 'sql', text: combinedText, values: combinedValues } as SQL
  Object.defineProperty(combinedSQLObject, internalSQLSymbol, {
    value: true,
    enumerable: false,
    writable: false,
  })

  return combinedSQLObject
}

export function groupBy(...expressions: (SQLInput | null)[]): SQL {
  const filteredExpressions = expressions.filter(truthy)
  if (filteredExpressions.length === 0) {
    return sql``
  }
  return sql`GROUP BY ${combineSQL(filteredExpressions, ', ')}`
}

/**
 * Cria uma cláusula WHERE a partir de uma lista de expressões.
 * Se nenhuma expressão for fornecida, retorna uma string vazia.
 * @param expressions - Expressões a serem combinadas com AND.
 * @returns Uma cláusula WHERE.
 */
export function where(...expressions: (SQLInput | null)[]): SQL {
  const filteredExpressions = expressions.filter(truthy)
  if (filteredExpressions.length === 0) {
    return sql``
  }
  return sql`WHERE ${combineSQL(filteredExpressions, 'AND')}`
}

export function orderBy(...expressions: (SQLInput | null)[]): SQL {
  const filteredExpressions = expressions.filter(truthy)
  if (filteredExpressions.length === 0) {
    return sql``
  }
  return sql`ORDER BY ${combineSQL(filteredExpressions, ', ')}`
}

// Função de verificação para garantir que o objeto SQL é genuíno
export function isInternalSQL(object: any): object is SQL {
  return (
    typeof object === 'object' &&
    object !== null &&
    object.type === 'sql' &&
    object[internalSQLSymbol] === true
  )
}

// Função de verificação para garantir que o objeto Preserve é genuíno
export function isInternalPreserve(object: any): object is Preserve {
  return (
    typeof object === 'object' &&
    object !== null &&
    object.type === 'preserve' &&
    object[internalPreserveSymbol] === true
  )
}

// Função de validação para garantir que os objetos são genuínos
export function validateSQL(sql: SQL) {
  if (!isInternalSQL(sql)) {
    throw errors.security('Tentativa de uso indevido rastreada e registrada')
  }
}

// SQL vazio
export const nothing = sql``

// Lida com arrays para uso em queries SQL
export function sqlArray(arr: string[] | undefined | null, type: 'uuid' | 'text' = 'uuid') {
  if (!arr || arr.length === 0) return sql`NULL`
  const cleanType = type.replace(/'/g, '')
  return sql`ARRAY[${combineSQL(
    arr.map(v => sql`${v}`),
    ', ',
  )}]::${preserve(`${cleanType}[]`)}`
}
